import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { formatRelativeDays, pluralize } from '../lib/format.js';
import { Avatar, Badge, Button, EmptyState } from '../components/ui.js';
import { SkeletonHeader, SkeletonLists } from '../components/Skeleton.js';
import { IconLock, IconSearch } from '../components/icons.js';

/**
 * Профиль другого человека и его списки.
 *
 * Заполняет дыру в навигации: раньше на имя друга нельзя было нажать, и увидеть
 * все его списки разом было негде — лента показывает их вперемешку с чужими.
 *
 * Какие списки видны, решает сервер по обычным правилам доступа: друг видит
 * списки «для друзей», посторонний — только публичные. Здесь мы просто рисуем
 * то, что пришло.
 */

export function UserPage(): JSX.Element {
  const { username = '' } = useParams();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['user', username],
    queryFn: () => api.users.profile(username),
    enabled: Boolean(username),
  });

  const addFriend = useMutation({
    mutationFn: (userId: string) => api.friends.request({ userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['user', username] });
      void queryClient.invalidateQueries({ queryKey: ['friends'] });
    },
  });

  /*
   * Профиль весь приезжает с данными — и имя, и списки, — поэтому заготовка
   * повторяет обе части: строку шапки и карточки списков. Кружок посередине
   * пустого экрана не сказал бы даже того, что здесь будет профиль человека.
   */
  if (isLoading) {
    return (
      <>
        <SkeletonHeader />
        <SkeletonLists label="Загружаем профиль" />
      </>
    );
  }

  if (error || !data) {
    return (
      <EmptyState
        icon={<IconSearch />}
        title="Человек не найден"
        text={
          error instanceof ApiError && error.status === 404
            ? 'Возможно, имя пользователя изменилось.'
            : 'Не удалось загрузить профиль.'
        }
      />
    );
  }

  const { user, lists } = data;

  return (
    <>
      <div className="page-header">
        <div className="row">
          <Avatar name={user.displayName} src={user.avatarUrl} size="lg" />
          <div>
            <h1>{user.displayName}</h1>
            {user.username && <p className="muted small">@{user.username}</p>}
            {user.bio && <p className="small" style={{ marginTop: 'var(--space-2)' }}>{user.bio}</p>}
          </div>
        </div>

        <div>
          {user.friendship?.status === 'accepted' ? (
            <Badge tone="success">Вы друзья</Badge>
          ) : user.friendship?.status === 'pending' ? (
            <Badge>{user.friendship.outgoing ? 'Заявка отправлена' : 'Ждёт вашего ответа'}</Badge>
          ) : (
            <Button
              variant="primary"
              loading={addFriend.isPending}
              onClick={() => addFriend.mutate(user.id)}
            >
              Позвать в друзья
            </Button>
          )}
        </div>
      </div>

      {lists.length === 0 ? (
        <EmptyState
          icon={<IconLock />}
          title="Списков не видно"
          text={
            user.friendship?.status === 'accepted'
              ? 'Пока ничего не создано или всё скрыто.'
              : 'Списки открыты только друзьям. Отправьте заявку — и они появятся.'
          }
        />
      ) : (
        <div className="stack">
          {lists.map((list) => (
            <Link key={list.id} to={`/lists/${list.id}`} className="card" style={{ display: 'block', color: 'inherit' }}>
              <div className="row" style={{ padding: 'var(--space-4)' }}>
                <span style={{ fontSize: '1.8rem' }} aria-hidden="true">
                  {list.coverEmoji || '🎁'}
                </span>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="strong truncate">{list.title}</div>
                  <div className="tiny muted">
                    {pluralize(list.itemCount, 'желание', 'желания', 'желаний')}
                    {list.eventDate && ` · ${formatRelativeDays(list.eventDate)}`}
                  </div>
                </div>
                <span className="faint" aria-hidden="true">
                  ›
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
